import { memo, SyntheticEvent, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';

import { Application } from '../../../../types';
import { useGetKaapApplications } from '../../../hooks';

const downloadApplicationDetails = (applications: Application[]) => {
  const header =
    '// For more details go to https://frontstage.tomtomgroup.com/docs/default/component/kaap-documentation';
  const fileContent = `${header}\n\n${JSON.stringify(applications, null, 2)}`;
  const blob = new Blob([fileContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'kaap-applications-details.jsonc';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type ApplicationSectionProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export const ApplicationSection = memo(
  ({ value, onChange, disabled }: ApplicationSectionProps) => {
    const { value: applications, loading } = useGetKaapApplications();
    const [error, setError] = useState(false);

    const description = (
      <span>
        Select the{' '}
        <Link
          target="_blank"
          underline="hover"
          style={{ cursor: 'pointer' }}
          onClick={() => downloadApplicationDetails(applications ?? [])}
        >
          application
        </Link>{' '}
        this tenant is for. Beware, this value can only be updated by contacting{' '}
        <Link
          target="_blank"
          underline="hover"
          href="https://tomtomslack.slack.com/archives/C039NQZUG7K"
        >
          #ccoe-finops
        </Link>
      </span>
    );

    const selectedApp =
      (applications ?? []).find(app => app.id === value) ?? null;
    const wrappedCCName = `(${selectedApp?.ccname})`;

    const onInternalChange = (
      _event: SyntheticEvent,
      app: Application | null,
    ) => {
      onChange(app?.id ?? '');
      setError(!app);
    };

    if (disabled) {
      const displayValue = selectedApp?.name ?? value ?? '';
      return (
        <FormControl fullWidth variant="standard">
          <TextField
            id="application"
            label="Application"
            size="small"
            variant="outlined"
            value={`${displayValue} ${selectedApp ? wrappedCCName : ''}`}
            disabled
            slotProps={{ htmlInput: { readOnly: true } }}
          />
          <FormHelperText>{description}</FormHelperText>
        </FormControl>
      );
    }

    return (
      <FormControl fullWidth variant="standard" required error={!value}>
        <Autocomplete
          id="application"
          size="small"
          value={selectedApp}
          loading={loading}
          options={[...(applications ?? [])].sort((a, b) =>
            a.buname.localeCompare(b.buname),
          )}
          groupBy={option => option.buname}
          getOptionLabel={option => `${option.name} (${option.ccname})`}
          getOptionKey={option => option.id}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          onChange={onInternalChange}
          renderInput={params => (
            <TextField
              {...params}
              label="Application"
              size="small"
              required
              variant="outlined"
              error={error}
            />
          )}
          noOptionsText="No applications found"
          loadingText="Loading applications..."
        />
        <FormHelperText error={error}>
          {error ? 'Application is required' : description}
        </FormHelperText>
      </FormControl>
    );
  },
);
